<template>
  <div class="flex items-center justify-space-between">
    <div class="flex pb-4 gap-8">
      <div class="flex flex-col gap-1 font-semibold">
        <p>{{ t('Date range') }}</p>

        <DateRangeMenu
          :activator="dateSelector ?? undefined"
          :selected-dates="consultationFilters.selectedRange"
          :show-selected-date="true"
          :show-remove-button="true"
          @preview-update="onDateRangePreviewUpdate"
          @range-update="onDateRangeUpdate"
          @remove-selection="onDateRangeRemove"
        />

        <div
          ref="dateSelector"
          class="bg-surface px-4 py-2 border !border-gray-300 rounded-lg flex gap-2 text-gray-500 font-semibold w-fit"
        >
          <VIcon
            class="material-icons-outlined"
            icon="calendar_today"
            size="24px"
            color="grey-400"
          />

          <p data-testid="date-range-label">
            <span v-if="!displayedRange.length">
              {{ t('Select date range') }}
            </span>

            <span v-else class="text-gray-700">
              <template v-if="displayedRange.length === 1">
                <span>{{ formatDateOnly(displayedRange[0]) }}</span>
                <span> – </span>
              </template>

              <template v-else>
                <span>{{ formatDateOnly(displayedRange[0]) }}</span>
                <span> – </span>
                <span>{{ formatDateOnly(displayedRange.at(-1)) }}</span>
              </template>
            </span>
          </p>

          <VIcon
            v-if="displayedRange.length"
            data-testid="remove-date-range-icon"
            class="material-icons-outlined"
            icon="close"
            size="24px"
            color="grey-400"
            @click.stop="onDateRangeRemove"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1 font-semibold w-80">
        <p>{{ t('Patients') }}</p>

        <ItemSelector
          :selected-items-ids="consultationFilters.selectedPatientsIds"
          :placeholder="t('Select patients')"
          :items="mappedPatients"
          :loading="patientsLoading"
          class="bg-surface"
          @selection-update="onPatientsUpdate"
          @remove-item="onPatientRemove"
        />
      </div>
    </div>

    <VBtn
      :text="t('Export consultation summary PDF')"
      data-testid="export-button"
      class="mt-4 font-semibold"
      @click="exportSummaryReports"
    />
  </div>

  <CompletedConsultationsList
    data-testid="completed-consultations-list"
    :is-error="error"
    :is-loading="isLoading"
    :items-per-page="itemsPerPage"
    :consultation-requests="consultations"
    :current-page="currentPage"
    :total-items="totalItems"
    @refetch="fetchCompletedConsultations"
    @options-update="updateOptionsAndFetch"
  />
</template>

<script setup lang="ts">
import ItemSelector, { type Selectable } from '@/components/common/ItemSelector.vue'
import DateRangeMenu from '@/components/common/DateRangeMenu.vue'
import CompletedConsultationsList from '@/components/consultationrequest/CompletedConsultationsList.vue'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useFormatDate } from '@/composables/useFormatDate'
import type { PatientDTO } from '@/generated'
import { useI18n } from 'vue-i18n'
import useSummaryReportExporter from '@/composables/useSummaryReportFetcher'
import useCompletedConsultationsTable from '@/composables/consultation/useCompletedConsultationsTable'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'

const {
  fetchCompletedConsultations,
  updateOptionsAndFetch,
  updateFiltersAndFetch,
  consultationFilters,
  error,
  isLoading,
  itemsPerPage,
  consultations,
  currentPage,
  totalItems
} = useCompletedConsultationsTable()
const { exportSummaryReports } = useSummaryReportExporter(consultationFilters)
const { formatDateOnly } = useFormatDate()
const { t } = useI18n()

const dateSelector = useTemplateRef<HTMLElement>('dateSelector')
const patientsToSelect = ref<PatientDTO[]>([])
const patientsLoading = ref<boolean>(false)
const previewSelectedRange = ref<Date[]>([])
const mappedPatients = computed<Selectable[]>(() =>
  patientsToSelect.value.map((patient) => ({
    id: patient.id,
    name: `${patient.firstName} ${patient.lastName}`
  }))
)

onMounted(async () => {
  await fetchCompletedConsultations()

  try {
    patientsLoading.value = true
    const response = await ConsultationService.getCompletedConsultationPatients()

    patientsToSelect.value = response.patients
  } catch (error) {
    useNoticeStore().add({
      type: NoticeType.ERROR,
      message: t('Failed to load available patients')
    })
  } finally {
    patientsLoading.value = false
  }
})

const displayedRange = computed(() =>
  previewSelectedRange.value.length
    ? previewSelectedRange.value
    : consultationFilters.value.selectedRange
)

function onPatientsUpdate(patientsIds: string[]) {
  updateFiltersAndFetch({
    ...consultationFilters.value,
    selectedPatientsIds: patientsIds
  })
}

function onPatientRemove(patientId: string) {
  const newPatientsIds = consultationFilters.value.selectedPatientsIds.filter(
    (id) => id !== patientId
  )
  onPatientsUpdate(newPatientsIds)
}

function onDateRangePreviewUpdate(range: Date[]) {
  previewSelectedRange.value = range
}

function onDateRangeUpdate(range: Date[]) {
  previewSelectedRange.value = range

  updateFiltersAndFetch({
    ...consultationFilters.value,
    selectedRange: range
  })
}

function onDateRangeRemove() {
  previewSelectedRange.value = []

  updateFiltersAndFetch({
    ...consultationFilters.value,
    selectedRange: []
  })
}
</script>

<style scoped></style>
